import { z } from 'zod';

import { quoteStatusSchema } from './quote-status.schema';

/**
 * Quote builder form schema (Create + Edit).
 *
 * ──────────────────────────────────────────────────────────────────────────
 *  PHASE 1 — Foundation (REWRITE_PLAN.md)
 * ──────────────────────────────────────────────────────────────────────────
 *  This schema is the FULL field set mirroring the legacy
 *  `src/services/quoteService.ts → QuoteItem` and `QuoteGroup` interfaces, so
 *  a quote round-trips through Create → API → Edit without data loss.
 *
 *  Pattern discipline (MIGRATION_PLAYBOOK §3.5): zod input == output.
 *  - Every property is required at the type level.
 *  - No `.default()` / `.optional()` — initial values come from
 *    `defaultQuoteFormValues` / `defaultQuoteLineItem` instead.
 *  - Numeric enums use `z.union([z.literal(1), ...])` so input == output.
 *  - Cross-field validation uses object-level `.refine` so field types stay
 *    clean (RHF Resolver compatibility).
 *  - Item-level business rules (at-most-one Labour, Structure dup, Fixed+Volume
 *    dimension checks) live in `@/utils/quoteValidation.ts` (reused as-is) —
 *    NOT in zod, so the form can still draft incomplete items.
 *
 *  Structure mirrors legacy `CreateQuote.tsx`:
 *    Header       — customerKind, ids, title, description, dates, terms
 *    Quote Jobs   — array of job groups, each with its own line items
 *    Variations   — array of post-award variations (shell-only in Wave 5)
 *    Notes/Terms  — free-text textarea
 */

// ===========================================================================
// Numeric-literal enums (mirror backend enums)
// ===========================================================================

/** Customer kind toggle — Lead vs Client. */
export const customerKindSchema = z.union([
  z.literal('lead'),
  z.literal('client'),
]);
export type QuoteCustomerKind = z.infer<typeof customerKindSchema>;

/** Scaffold task phase. Quote module uses 1=Erect, 3=Dismantle only. */
export const scaffoldTaskTypeSchema = z.union([
  z.literal(1),
  z.literal(3),
]);
export type QuoteFormScaffoldTaskType = z.infer<typeof scaffoldTaskTypeSchema>;

/**
 * Quote UI item type (mirrors legacy UI mapping; API translation lives in
 * `constants/WorkItemType.ts`):
 *   1 = Rental (legacy, NOT creatable via new UI — kept for round-trip parity)
 *   2 = Labour (API itemType=1)
 *   3 = Logistics (API itemType=3)
 *   4 = Custom (API itemType=4)
 *   5 = Structure (API itemType=2)
 */
export const quoteItemTypeSchema = z.union([
  z.literal(1),
  z.literal(2),
  z.literal(3),
  z.literal(4),
  z.literal(5),
]);
export type QuoteFormItemType = z.infer<typeof quoteItemTypeSchema>;

/**
 * Labour calculation type (API enum values):
 *   0 = unset (UI-only sentinel), 1 = Fixed, 2 = PerHour, 3 = PerDay,
 *   4 = VolumeBased, 5 = RoleBased.
 *
 * Legacy UI mostly creates VolumeBased (=4). PerHour/PerDay/RoleBased values
 * appear only in historic edits and are preserved as read-only.
 */
export const labourCalculationTypeSchema = z.union([
  z.literal(0),
  z.literal(1),
  z.literal(2),
  z.literal(3),
  z.literal(4),
  z.literal(5),
]);
export type LabourCalculationTypeValue = z.infer<typeof labourCalculationTypeSchema>;

/**
 * Structure pricing method (legacy field `structurePricingMethod`):
 *   0 = unset, 1 = Fixed, 2 = ProductBased, 3 = Hybrid (Fixed + Product),
 *   4 = FixedVolume (Fixed + Volume / dimension-based).
 */
export const structurePricingMethodSchema = z.union([
  z.literal(0),
  z.literal(1),
  z.literal(2),
  z.literal(3),
  z.literal(4),
]);
export type StructurePricingMethodValue = z.infer<typeof structurePricingMethodSchema>;

/**
 * UI-only label for structure pricing method — preserved on edit so the
 * AddItemModal can re-open in the same step (`Fixed | ProductBase | Dynamic |
 * FixedVolume`).
 */
export const uiPricingMethodSchema = z.union([
  z.literal(''),
  z.literal('Fixed'),
  z.literal('ProductBase'),
  z.literal('Dynamic'),
  z.literal('FixedVolume'),
]);
export type UiPricingMethodValue = z.infer<typeof uiPricingMethodSchema>;

/**
 * Cartage vehicle type for Logistics items.
 *
 * Mirrors backend `CommonEnums.CartageVehicleType` (see the canonical FE list
 * in `@/shared/constants/cartage-vehicle-type`):
 *   0 = unset (UI sentinel), 1 = Ute, 2 = Ute + Trailer, 3 = Small Truck,
 *   4 = Large Truck, 5 = Trailer, 6 = Class 4, 99 = Other.
 *
 * The Logistics sub-form renders the full set so the per-km cartage-rate
 * matrix (which keys off every vehicle type) can resolve a rate for any pick.
 */
export const cartageVehicleTypeSchema = z.union([
  z.literal(0),
  z.literal(1),
  z.literal(2),
  z.literal(3),
  z.literal(4),
  z.literal(5),
  z.literal(6),
  z.literal(99),
]);
export type CartageVehicleTypeValue = z.infer<typeof cartageVehicleTypeSchema>;

/**
 * Custom item category.
 *   0 = General, 1 = ExternalInspection, 2 = PrimeCost, 3 = Other.
 */
export const customItemCategorySchema = z.union([
  z.literal(0),
  z.literal(1),
  z.literal(2),
  z.literal(3),
]);
export type CustomItemCategoryValue = z.infer<typeof customItemCategorySchema>;

/**
 * Custom rate type (legacy `customRateType` / UI `displayRentalRateType`).
 *   0 = unset, 1 = FixedAmount, 2 = PerHour, 3 = PerDay, 4 = PerWeek,
 *   5 = PerVisit.
 */
export const customRateTypeSchema = z.union([
  z.literal(0),
  z.literal(1),
  z.literal(2),
  z.literal(3),
  z.literal(4),
  z.literal(5),
]);
export type CustomRateTypeValue = z.infer<typeof customRateTypeSchema>;

/**
 * Recurrence pattern (legacy `recurrencePattern` / payload `billingFrequency`).
 *   0 = unset, 1 = Weekly, 2 = Monthly.
 */
export const recurrencePatternSchema = z.union([
  z.literal(0),
  z.literal(1),
  z.literal(2),
]);
export type RecurrencePatternValue = z.infer<typeof recurrencePatternSchema>;

/**
 * Rental / Compliance calculation type.
 *   1 = Fixed, 2 = % of Erect, 3 = % of Dismantle, 4 = % of Total,
 *   5 = % of Labour.
 */
export const rentalCalculationTypeSchema = z.union([
  z.literal(1),
  z.literal(2),
  z.literal(3),
  z.literal(4),
  z.literal(5),
]);
export type RentalCalculationTypeValue = z.infer<typeof rentalCalculationTypeSchema>;

/** Rental / Compliance frequency. 1=Daily, 2=Weekly, 3=Monthly. */
export const rentalFrequencySchema = z.union([
  z.literal(1),
  z.literal(2),
  z.literal(3),
]);
export type RentalFrequencyValue = z.infer<typeof rentalFrequencySchema>;

/** Payment terms — free-form bucket. */
export const paymentTermsOptions = [
  '',
  'Net 7',
  'Net 14',
  'Net 30',
  'Due on receipt',
  'Custom',
] as const;
export const paymentTermsSchema = z.enum(paymentTermsOptions);
export type PaymentTermsOption = z.infer<typeof paymentTermsSchema>;

// ===========================================================================
// Nested arrays — Product line items (Structure) + Labour role rates
// ===========================================================================

/**
 * Structure product line — one row in a Product-Based / Hybrid Structure item.
 *
 * Mirrors legacy `ProductLineItem` (`src/services/quoteService.ts`).
 */
export const quoteStructureLineItemSchema = z.object({
  id: z.string(),
  productId: z.string(),
  productName: z.string(),
  quantity: z.number().min(0, 'Quantity must be 0 or greater'),
  rate: z.number().min(0, 'Rate must be 0 or greater'),
});
export type QuoteStructureLineItem = z.infer<typeof quoteStructureLineItemSchema>;

export const defaultQuoteStructureLineItem: QuoteStructureLineItem = {
  id: '',
  productId: '',
  productName: '',
  quantity: 0,
  rate: 0,
};

/**
 * Labour role line — one row in a Role-Based Labour item (legacy data only;
 * new items use VolumeBased). Mirrors legacy `LabourLineItem`.
 */
export const quoteLabourLineItemSchema = z.object({
  id: z.string(),
  roleId: z.string(),
  roleName: z.string(),
  quantity: z.number().min(0, 'Staff count must be 0 or greater'),
  hours: z.number().min(0, 'Hours must be 0 or greater'),
  ratePerHour: z.number().min(0, 'Rate per hour must be 0 or greater'),
  scaffoldTaskType: scaffoldTaskTypeSchema,
});
export type QuoteLabourLineItem = z.infer<typeof quoteLabourLineItemSchema>;

export const defaultQuoteLabourLineItem: QuoteLabourLineItem = {
  id: '',
  roleId: '',
  roleName: '',
  quantity: 0,
  hours: 0,
  ratePerHour: 0,
  scaffoldTaskType: 1,
};

// ===========================================================================
// Line item schema — full legacy QuoteItem field set
// ===========================================================================

/**
 * Quote line item — full legacy `QuoteItem` field set.
 *
 * Most fields are conditionally-meaningful (only the per-itemType subset is
 * used) but ALL are present at the type level so input == output and the form
 * can edit any legacy quote without data loss. Per-itemType validation lives
 * in `@/utils/quoteValidation.ts` and the per-step AddItemDialog.
 */
export const quoteLineItemSchema = z.object({
  // Identity ───────────────────────────────────────────────
  id: z.string(),
  quoteJobId: z.string(),

  // Common ────────────────────────────────────────────────
  itemType: quoteItemTypeSchema,
  scaffoldTaskType: scaffoldTaskTypeSchema,
  taskDescription: z
    .string()
    .min(1, 'Description is required')
    .max(500, 'Description must be 500 characters or fewer'),
  quantity: z.number().min(0, 'Quantity must be 0 or greater'),
  finalUnitPrice: z.number().min(0, 'Unit price must be 0 or greater'),
  discountPercent: z
    .number()
    .min(0, 'Discount cannot be negative')
    .max(100, 'Discount cannot exceed 100'),
  notes: z.string().max(500, 'Notes must be 500 characters or fewer'),

  // LABOUR (itemType=2) ───────────────────────────────────
  labourCalculationType: labourCalculationTypeSchema,
  labourRate: z.number().min(0),
  labourHours: z.number().min(0),
  fixedMobilisationCharge: z.number().min(0),
  extraAccessCost: z.number().min(0),
  roleId: z.string(),
  labourLineItems: z.array(quoteLabourLineItemSchema),

  // STRUCTURE (itemType=5) ────────────────────────────────
  structurePricingMethod: structurePricingMethodSchema,
  uiPricingMethod: uiPricingMethodSchema,
  structureFixedPrice: z.number().min(0),
  structureLineItems: z.array(quoteStructureLineItemSchema),

  // SHARED dimensions (Structure FixedVolume + Labour VolumeBased) ────
  length: z.number().min(0),
  depth: z.number().min(0),
  height: z.number().min(0),
  volumeBasedLabourRate: z.number().min(0),

  // LOGISTICS (itemType=3) ────────────────────────────────
  cartageVehicleType: cartageVehicleTypeSchema,
  distanceKM: z.number().min(0),
  cartageRate: z.number().min(0),

  // CUSTOM (itemType=4) ───────────────────────────────────
  customItemCategory: customItemCategorySchema,
  displayRentalRateType: customRateTypeSchema,
  genericRate: z.number().min(0),
  weeklyRate: z.number().min(0),
  isRecurring: z.boolean(),
  recurrencePattern: recurrencePatternSchema,

  // RENTAL (itemType=1 — legacy data only) ────────────────
  isPriceDerivedFromProduct: z.boolean(),
  rentalRate: z.number().min(0),
});

export type QuoteLineItemValues = z.infer<typeof quoteLineItemSchema>;

export const defaultQuoteLineItem: QuoteLineItemValues = {
  // Identity
  id: '',
  quoteJobId: '',

  // Common defaults — Structure(Erect) is the legacy first-add flow.
  itemType: 5,
  scaffoldTaskType: 1,
  taskDescription: '',
  quantity: 1,
  finalUnitPrice: 0,
  discountPercent: 0,
  notes: '',

  // Labour
  labourCalculationType: 0,
  labourRate: 0,
  labourHours: 0,
  fixedMobilisationCharge: 0,
  extraAccessCost: 0,
  roleId: '',
  labourLineItems: [],

  // Structure
  structurePricingMethod: 0,
  uiPricingMethod: '',
  structureFixedPrice: 0,
  structureLineItems: [],

  // Dimensions
  length: 0,
  depth: 0,
  height: 0,
  volumeBasedLabourRate: 0,

  // Logistics
  cartageVehicleType: 0,
  distanceKM: 0,
  cartageRate: 0,

  // Custom
  customItemCategory: 0,
  displayRentalRateType: 0,
  genericRate: 0,
  weeklyRate: 0,
  isRecurring: false,
  // Legacy default: Monthly (2). Even when isRecurring=false, when the
  // user flips the toggle ON the legacy showed "Monthly" pre-selected.
  // Keeps the new flow consistent with operator muscle memory.
  recurrencePattern: 2,

  // Rental (legacy)
  isPriceDerivedFromProduct: false,
  rentalRate: 0,
};

// ===========================================================================
// Quote Job (group of items) schema — mirrors legacy QuoteGroup
// ===========================================================================

export const quoteJobSchema = z.object({
  id: z.string(),
  name: z
    .string()
    .min(1, 'Work title is required')
    .max(200, 'Work title must be 200 characters or fewer'),
  description: z
    .string()
    .max(1000, 'Description must be 1000 characters or fewer'),
  sequence: z.number().int().min(0),

  /** Rental config (per-job, accrues independently from item totals). */
  rentalCalculationType: rentalCalculationTypeSchema,
  rentalConfigValue: z.number().min(0, 'Rental config must be 0 or greater'),
  rentalFrequency: rentalFrequencySchema,

  /** Compliance fee config (calc type is always Fixed (=1) in legacy UI). */
  complianceCalculationType: rentalCalculationTypeSchema,
  complianceConfigValue: z.number().min(0, 'Compliance config must be 0 or greater'),
  complianceFrequency: rentalFrequencySchema,

  /** Job linkage (Site → Job picker; populated when quote is for an existing WO job). */
  jobReferenceNumber: z.string(),

  /** Line items — at least one required (zod-level; per-itemType rules live elsewhere). */
  // Min(1) is enforced at submit via `validateQuote()` (legacy parity) so the
  // form can mount with empty jobs and the empty-state CTA is visible.
  items: z.array(quoteLineItemSchema),
});

export type QuoteJobValues = z.infer<typeof quoteJobSchema>;

export const defaultQuoteJob: QuoteJobValues = {
  id: '',
  name: '',
  description: '',
  sequence: 0,
  rentalCalculationType: 1,
  rentalConfigValue: 0,
  rentalFrequency: 2,
  complianceCalculationType: 1,
  complianceConfigValue: 0,
  complianceFrequency: 2,
  jobReferenceNumber: '',
  // Empty by default (legacy parity). Submit-time validation in
  // `validateQuote()` enforces "each job must have ≥ 1 item".
  items: [],
};

// ===========================================================================
// Variations (post-award change-orders) — shell only
// ===========================================================================

/**
 * Variation row — a post-award price up/down record. Wave 5 ships the shell;
 * full variation editing arrives post-Wave-5.
 *
 * TODO(post-Wave-5): expand into nested useFieldArray of line items.
 */
export const quoteVariationSchema = z.object({
  id: z.string(),
  title: z.string().max(200, 'Title must be 200 characters or fewer'),
  reason: z.string().max(1000, 'Reason must be 1000 characters or fewer'),
  /** Net adjustment amount (positive = price up, negative = price down). */
  netAmount: z.number(),
});

export type QuoteVariationValues = z.infer<typeof quoteVariationSchema>;

export const defaultQuoteVariation: QuoteVariationValues = {
  id: '',
  title: '',
  reason: '',
  netAmount: 0,
};

// ===========================================================================
// Top-level Quote form schema
// ===========================================================================

const baseQuoteFormSchema = z.object({
  // Party selection ────────────────────────────────────────
  customerKind: customerKindSchema,
  leadId: z.string(),
  clientId: z.string(),
  siteId: z.string(),
  contactId: z.string(),
  jobId: z.string(),

  // Identification ────────────────────────────────────────
  quoteNumber: z.string(),
  jobNumber: z.string(),
  /** UI flag: Auto-generate quote # vs Manual. Default Auto on create. */
  isQuoteNumberAuto: z.boolean(),

  // Project basics ────────────────────────────────────────
  quoteTitle: z
    .string()
    .min(1, 'Title is required')
    .max(200, 'Title must be 200 characters or fewer'),
  jobDescription: z
    .string()
    .min(1, 'Description / Scope is required')
    .max(2000, 'Description must be 2000 characters or fewer'),

  // Dates ─────────────────────────────────────────────────
  quoteDate: z.string().min(1, 'Quote date is required'),
  validUntilDate: z.string().min(1, 'Valid until date is required'),

  /**
   * Estimated scaffold hire duration in weeks (0 = not estimated). Drives the
   * ballpark rental forecast on the Quote Conversion Report. Not part of
   * pricing — purely an operator forecast input.
   */
  estimatedDurationWeeks: z.number().int().min(0).max(520),

  // Commercial ────────────────────────────────────────────
  paymentTerms: paymentTermsSchema,
  assignedSupervisorId: z.string().min(1, 'Assigned supervisor is required'),
  notes: z.string().max(2000, 'Notes must be 2000 characters or fewer'),

  // Body ──────────────────────────────────────────────────
  quoteJobs: z.array(quoteJobSchema).min(1, 'Add at least one quote job'),
  variations: z.array(quoteVariationSchema),

  // Workflow ──────────────────────────────────────────────
  /**
   * Backend status. 0 on Create (no status yet); backend assigns Draft (=1)
   * on submit. `0` is a sentinel literal that participates in the type so
   * input == output (RHF compatibility).
   */
  status: z.union([z.literal(0), quoteStatusSchema]),
});

/**
 * Cross-field validation:
 *   - `validUntilDate >= quoteDate`
 *   - `leadId` required when `customerKind === 'lead'`
 *   - `clientId` + `siteId` required when `customerKind === 'client'`
 *
 * Item-level rules (at-most-one Labour per job, Structure duplicate, Fixed+Volume
 * dimension check, etc.) live in `@/utils/quoteValidation.ts` — call
 * `validateQuote()` from the submit handler.
 */
export const quoteFormSchema = baseQuoteFormSchema
  .refine(
    (data) =>
      !data.validUntilDate ||
      !data.quoteDate ||
      data.validUntilDate >= data.quoteDate,
    {
      message: 'Valid until must be on or after the quote date',
      path: ['validUntilDate'],
    },
  )
  .refine(
    (data) => data.customerKind !== 'lead' || !!data.leadId,
    {
      message: 'Please select a Lead',
      path: ['leadId'],
    },
  )
  .refine(
    (data) => data.customerKind !== 'client' || !!data.clientId,
    {
      message: 'Please select a Client',
      path: ['clientId'],
    },
  )
  .refine(
    (data) => data.customerKind !== 'client' || !!data.siteId,
    {
      message: 'Please select a Site',
      path: ['siteId'],
    },
  )
  .refine(
    // Schema-level guard: at most one Labour (itemType=2) per job. The
    // "≥ 1 item per job" rule is enforced at submit via `validateQuote()`
    // (so the form can start with an empty job and show the empty-state).
    (data) =>
      data.quoteJobs.every(
        (job) => job.items.filter((i) => i.itemType === 2).length <= 1,
      ),
    {
      message: 'Only one Labour item is allowed per job.',
      path: ['quoteJobs'],
    },
  );

export type QuoteFormValues = z.infer<typeof quoteFormSchema>;

// ===========================================================================
// Defaults
// ===========================================================================

/** Today's date in YYYY-MM-DD (local time). */
function todayIso(): string {
  return new Date().toISOString().slice(0, 10);
}

/** Today + N days. */
function todayPlusDaysIso(days: number): string {
  const d = new Date();
  d.setDate(d.getDate() + days);
  return d.toISOString().slice(0, 10);
}

export const defaultQuoteFormValues: QuoteFormValues = {
  // Legacy parity: legacy form initialises `activeTab='1'` (New Lead).
  customerKind: 'lead',
  leadId: '',
  clientId: '',
  siteId: '',
  contactId: '',
  jobId: '',
  quoteNumber: '',
  jobNumber: '',
  isQuoteNumberAuto: true,
  quoteTitle: '',
  jobDescription: '',
  quoteDate: todayIso(),
  validUntilDate: todayPlusDaysIso(90),
  estimatedDurationWeeks: 0,
  paymentTerms: '',
  assignedSupervisorId: '',
  notes: '',
  quoteJobs: [{ ...defaultQuoteJob }],
  variations: [],
  status: 0,
};
